import { FUNDING_TYPES, FUNDING_TYPE_SHORT_NAMES } from 'universal'
import { actionTypes } from '../../constants'
import { MessageFormat } from '@paypalcorp/worldready'
import { getNetworkStatus, isValidIntent } from 'utils/utils'
import { getQueryParam } from 'utils/query'
import { isFlowForRequestPhysicalCard as isRequestPhysicalCardFlow } from 'utils/flow'
import { PRODUCT_NAMES, OPERATIONS } from 'universal/cfs-card'
import { isShowCardAllowed } from '@/screens/bdmc-summary/CardDetails/utils/cardHelpers'
import { REPLACE_CARD_REASONS } from '@/constants/card'
import { FLOW_STEPS } from '@/constants/flow-steps'

const { BUSINESS_DEBIT_CARD } = PRODUCT_NAMES
const { LOST_OR_STOLEN, LOST, STOLEN, NAME_CHANGE } = REPLACE_CARD_REASONS
const { REPORT_CARD_LOST, REISSUE } = OPERATIONS
const { CHOOSE_ADDRESS_STEP, NORMALIZED_ADDRESS_STEP, INVALID_ADDRESS_STEP, ADD_ADDRESS_STEP } =
  FLOW_STEPS.CHANGE_ADDRESS
const { SET_PIN_STEP } = FLOW_STEPS.SET_PIN

const {
  CANCEL_CARD,
  CHANGE_PIN,
  VIEW_PIN,
  CHANGE_CVV,
  SET_FUNDING,
  REMOVE_FUNDING,
  CREATE_CARD,
  APPLY_CARD,
  FETCH_ADDRESSES,
  FETCH_CARDS,
  FETCH_CARD,
  SHOW_CLASSIFIED_CARD_DATA,
  FETCH_FUNDING_SUMMARY,
  FETCH_FUNDING_DETAILS,
  REACTIVATE_CARD,
  DEACTIVATE_CARD,
} = actionTypes

const generic = 'generic'
const noInternet = 'noInternet'

const REQUEST_PHYSICAL_CARD_MODAL_TITLE_ID =
  'changeAddress.chooseAddress.requestPhysicalCard.modalTitle'

function isRequestPhysicalCardAddressFlow(productName: string, addressType: string) {
  return (
    productName === BUSINESS_DEBIT_CARD &&
    (isRequestPhysicalCardFlow() || addressType.toLowerCase() === 'shipping')
  )
}

// eslint-disable-next-line complexity
function getActionName(actionType) {
  let contentId
  switch (actionType) {
    case CANCEL_CARD:
      contentId = 'cancelCard'
      break
    case CHANGE_PIN:
      contentId = 'changePin'
      break
    case VIEW_PIN:
      contentId = 'viewPin'
      break
    case CHANGE_CVV:
      contentId = 'changeCvv'
      break
    case SET_FUNDING:
      contentId = 'setFunding'
      break
    case REMOVE_FUNDING:
      contentId = 'removeFunding'
      break
    case CREATE_CARD:
      contentId = 'createCard'
      break
    case APPLY_CARD:
      contentId = 'applyCard'
      break
    case FETCH_ADDRESSES:
      contentId = 'fetchAddresses'
      break
    case FETCH_CARDS:
      contentId = 'fetchCards'
      break
    case FETCH_CARD:
      contentId = 'fetchCard'
      break
    case SHOW_CLASSIFIED_CARD_DATA:
      contentId = 'showClassifiedCardData'
      break
    case FETCH_FUNDING_SUMMARY:
      contentId = 'fetchFundingSummary'
      break
    case FETCH_FUNDING_DETAILS:
      contentId = 'fetchFundingDetails'
      break
    case DEACTIVATE_CARD:
      contentId = 'lock'
      break
    case REACTIVATE_CARD:
      contentId = 'unlock'
      break
    default:
      contentId = generic
  }
  return contentId
}

function hasNonGenericContentKey(actionType) {
  return [
    CANCEL_CARD,
    CHANGE_PIN,
    VIEW_PIN,
    CHANGE_CVV,
    SET_FUNDING,
    REMOVE_FUNDING,
    CREATE_CARD,
  ].includes(actionType)
}

function getRetryContentId(type, actionType) {
  const isOffline = !getNetworkStatus()

  let contentId = generic
  if (isOffline) {
    contentId = noInternet
  } else if (hasNonGenericContentKey(actionType)) {
    contentId = getActionName(actionType)
  }
  return `error.retry.${contentId}.${type}`
}

function getRetryErrorContentId(type, actionType) {
  const contentId = hasNonGenericContentKey(actionType) ? getActionName(actionType) : generic
  return `error.retryError.${contentId}.${type}`
}

function getFundingOptionTypeContentId(fundingOptionType) {
  let contentId
  if (Object.keys(FUNDING_TYPES.ATU).includes(fundingOptionType)) {
    contentId = FUNDING_TYPE_SHORT_NAMES.ATU
  } else if (Object.keys(FUNDING_TYPES.BUFS).includes(fundingOptionType)) {
    contentId = FUNDING_TYPE_SHORT_NAMES.BUFS
  } else {
    contentId = generic
  }
  return `common.link.funding.${contentId}`
}

// Check for unlocalized string, e.g. [[Back]] or common.nav.dev
function isContentLocalized(string) {
  return !string.match(/^\[\[[\s\S]*]]$/) || string.match(/^\w+(\.\w+)*$/) //NOSONAR
}

function getContentForWindowPromptFallbackForCopy(props) {
  const { worldReady = {}, shortProductName = '' } = props
  return {
    // message for window.prompt fallback
    message: new MessageFormat(worldReady, 'showCard.cardDetails.copyManually'.split('.')).format({
      shortProductName,
      // copy-to-clipboard will replace this with ⌘/Ctrl+C
      copyShortcutPlaceholder: '#{key}',
    }),
  }
}

function getReissueContentIds({
  isPayrollProviderFlow,
  isDigitalOnlyCard,
  productName,
  selectedReason = '',
}) {
  let titleId = ''
  if (isPayrollProviderFlow) {
    titleId = 'replaceCard.reissueCardDigitalOnly.payrollProvider.title'
  } else if (!isDigitalOnlyCard && productName === BUSINESS_DEBIT_CARD) {
    titleId = 'churnflow.modal.title.replace'
  }

  const isBDMCPhysical =
    !isDigitalOnlyCard && !isPayrollProviderFlow && productName === BUSINESS_DEBIT_CARD

  let header, address, button, description
  if (isDigitalOnlyCard) {
    header = 'replaceCard.reissueCardDigitalOnly.header'
    address = ''
    button = 'replaceCard.button.getPhysicalCard'
  } else if (isBDMCPhysical) {
    header = 'churnflow.replace.damaged.title'
    address = 'churnflow.replace.general.send.replacement.card'
    button = 'churnflow.replace.damaged.button'
    description =
      selectedReason === NAME_CHANGE
        ? 'churnflow.name-change.primary.messages.one'
        : 'churnflow.replace.damaged.messages.one'
  } else {
    header = 'replaceCard.replaceDamaged.header'
    address = 'replaceCard.addressPanel.sendCardTo'
    button = 'replaceCard.button.sendReplacementCard'
  }

  return {
    header,
    headerAlign: 'center',
    address,
    addressLink: isBDMCPhysical ? 'churnflow.replace.general.update-address' : '',
    button,
    description: isBDMCPhysical ? description : '',
    title:
      isDigitalOnlyCard && productName === BUSINESS_DEBIT_CARD
        ? 'replaceCard.reissueCardDigitalOnly.bdmc.title'
        : titleId,
  }
}

function getBdmcReportLostBodyMessageIds(
  isStolen: boolean,
  card?: { capabilities?: string[] } | null,
) {
  if (isStolen) {
    return {
      headerSubtitle: '',
      bodyAfterAddress: 'churnflow.replace.stolen.messages.one',
      bodyTwo: 'churnflow.replace.stolen.messages.two',
      bodyTwoUsesCardSuffix: false,
    }
  }

  if (card && isShowCardAllowed(card)) {
    return {
      headerSubtitle: '',
      bodyAfterAddress: 'churnflow.replace.missing.message.one',
      bodyTwo: 'churnflow.replace.missing.message.two',
      bodyTwoUsesCardSuffix: false,
    }
  }

  return {
    headerSubtitle: 'churnflow.replace.missing.messages.one',
    bodyAfterAddress: '',
    bodyTwo: 'churnflow.replace.general.canceled',
    bodyTwoUsesCardSuffix: true,
  }
}

function getReportCardLostContentIds({
  isDigitalOnlyCard,
  productName,
  selectedReason = LOST,
  card,
}) {
  const reason = getQueryParam('reason')
  let titleId = ''
  let headerMessageId = 'replaceCard.reportLostStolen.header'
  const headerAlign = 'center'
  let bodyMessageId = 'replaceCard.reportLostStolen.content'
  let buttonMessageId = 'replaceCard.button.sendReplacementCard'

  if (isDigitalOnlyCard) {
    if (productName === BUSINESS_DEBIT_CARD && reason === LOST_OR_STOLEN) {
      headerMessageId = 'replaceCard.reportStolenDigitalOnly.bdmc.header'
      bodyMessageId = 'replaceCard.reportStolenDigitalOnly.bdmc.content'
      buttonMessageId = 'replaceCard.button.bdmc.reportStolenDigitalOnly'
    } else {
      headerMessageId = 'replaceCard.reportStolenDigitalOnly.header'
      bodyMessageId = 'replaceCard.reportStolenDigitalOnly.content'
      buttonMessageId = 'replaceCard.button.reportStolenDigitalOnly'
      titleId = 'replaceCard.reportStolenDigitalOnly.modalTitle'
    }
  } else if (productName === BUSINESS_DEBIT_CARD) {
    titleId = 'churnflow.modal.title.replace'
    const isStolen = selectedReason === STOLEN
    headerMessageId = isStolen
      ? 'churnflow.replace.stolen.title'
      : 'churnflow.replace.missing.title'
    bodyMessageId = ''
    buttonMessageId = isStolen
      ? 'components.v2.strings.churnflow.replace.stolen.button'
      : 'churnflow.replace.missing.button'
  }

  const isBDMCPhysical = !isDigitalOnlyCard && productName === BUSINESS_DEBIT_CARD
  const isStolen = selectedReason === STOLEN
  const bdmcBodyMessageIds = isBDMCPhysical ? getBdmcReportLostBodyMessageIds(isStolen, card) : null

  return {
    header: headerMessageId,
    headerAlign,
    body: bodyMessageId,
    button: buttonMessageId,
    title: titleId,
    address: isBDMCPhysical ? 'churnflow.replace.general.send.replacement.card' : '',
    addressLink: isBDMCPhysical ? 'churnflow.replace.general.update-address' : '',
    headerSubtitle: bdmcBodyMessageIds?.headerSubtitle ?? '',
    bodyAfterAddress: bdmcBodyMessageIds?.bodyAfterAddress ?? '',
    bodyTwo: bdmcBodyMessageIds?.bodyTwo ?? '',
    bodyTwoUsesCardSuffix: bdmcBodyMessageIds?.bodyTwoUsesCardSuffix ?? false,
  }
}

function getContentIdsByReplaceCardType({
  isPayrollProviderFlow,
  isDigitalOnlyCard,
  pageType,
  productName = '',
  selectedReason = '',
  card = null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): any {
  switch (pageType) {
    case REISSUE:
      return getReissueContentIds({
        isPayrollProviderFlow,
        isDigitalOnlyCard,
        productName,
        selectedReason,
      })
    case REPORT_CARD_LOST:
      return getReportCardLostContentIds({
        isDigitalOnlyCard,
        productName,
        selectedReason,
        card,
      })
    default:
      return { title: '' }
  }
}

function getContentIdsBySetPinPageType(
  pageType,
  productName = '',
  isFlowForRequestPhysicalCard = false,
) {
  let contentIds
  switch (pageType) {
    case SET_PIN_STEP:
      const contentIdPrefix = `setPin.createPin${
        productName === BUSINESS_DEBIT_CARD ? '.bdmc' : ''
      }`

      contentIds = {
        modalTitle: `${contentIdPrefix}.modalTitle`,
        header: !isFlowForRequestPhysicalCard
          ? 'changePin.changePin.header'
          : `${contentIdPrefix}.header`,
        description: `${contentIdPrefix}.description`,
      }
      break
    default:
      contentIds = {}
  }
  return contentIds
}

function getContentIdsByAddressAndPageType(type, pageType, productName = '', _extra?: any) {
  if (!type) return ''

  let contentIdPrefix
  let contentIds
  let titleContent = ''
  const addressType = type.toLowerCase()
  const intent = getQueryParam('intent')
  const isRequestPhysicalCardAddress = isRequestPhysicalCardAddressFlow(productName, addressType)

  switch (pageType) {
    case CHOOSE_ADDRESS_STEP: {
      contentIdPrefix = `changeAddress.chooseAddress.${addressType}`
      const headerId = `${contentIdPrefix}${isValidIntent(intent) ? `.${intent}` : ''}.header`
      titleContent = isRequestPhysicalCardAddress
        ? REQUEST_PHYSICAL_CARD_MODAL_TITLE_ID
        : `${contentIdPrefix}.modalTitle`
      contentIds = {
        header: isRequestPhysicalCardAddress
          ? 'changeAddress.chooseAddress.requestPhysicalCard.header'
          : headerId,
        addAddress: `changeAddress.link.${addressType}.addAddress`,
        save: 'changeAddress.button.save',
        confirm: 'changeAddress.button.confirm',
        modalTitle: titleContent,
      }
      break
    }
    case NORMALIZED_ADDRESS_STEP:
      contentIdPrefix = `changeAddress.normalizedAddress.${addressType}`
      contentIds = {
        header: `${contentIdPrefix}.header`,
        matchedAddresses: `${contentIdPrefix}.matchedAddresses`,
        enteredAddress: `${contentIdPrefix}.enteredAddress`,
        editAddress: 'changeAddress.link.editAddress',
        save: 'changeAddress.button.save',
        modalTitle:
          isRequestPhysicalCardAddress || isRequestPhysicalCardFlow()
            ? REQUEST_PHYSICAL_CARD_MODAL_TITLE_ID
            : `${contentIdPrefix}.modalTitle`,
      }
      break
    case INVALID_ADDRESS_STEP:
      contentIdPrefix = `changeAddress.invalidAddress.${addressType}`
      contentIds = {
        header: `${contentIdPrefix}.header`,
        addAddress: 'changeAddress.button.addANewAddress',
        useThisAddress: 'changeAddress.button.useThisAddress',
        modalTitle: isRequestPhysicalCardFlow()
          ? REQUEST_PHYSICAL_CARD_MODAL_TITLE_ID
          : `${contentIdPrefix}.modalTitle`,
      }
      break
    case ADD_ADDRESS_STEP:
      contentIdPrefix = `changeAddress.addAddress.${addressType}`
      titleContent = isRequestPhysicalCardAddress
        ? 'changeAddress.addAddress.requestPhysicalCard.modalTitle'
        : `${contentIdPrefix}.modalTitle`
      contentIds = {
        modalTitle: titleContent,
        header: 'changeAddress.addAddress.form.header',
        description: 'changeAddress.addAddress.form.description',
        submitButton: 'changeAddress.button.save',
      }
      break
    default:
      contentIds = {
        modalTitle: titleContent,
      }
  }
  return contentIds
}

function getMessageFormatter(worldReady) {
  return function (key, options = {}) {
    if (!worldReady || !key) return ''

    return new MessageFormat(worldReady, key.split('.')).format(options)
  }
}

const getFormatMessageObject = (id, variables = {}) => {
  return {
    id,
    variables,
  }
}

const formatContentMessage = (contentKey, worldReady) => {
  const formatMessage = getMessageFormatter(worldReady)
  return formatMessage(contentKey)
}

//Create content message id by appending countrycode followed by '_'
const formatCountrySpecificContentId = (contentId, countryCode) => {
  const start = contentId.substring(0, contentId.lastIndexOf('.') + 1)
  const last = contentId.substring(contentId.lastIndexOf('.') + 1, contentId.length)

  return `${start}${countryCode}_${last}`
}

export {
  getActionName,
  getRetryContentId,
  getRetryErrorContentId,
  getFundingOptionTypeContentId,
  isContentLocalized,
  getContentForWindowPromptFallbackForCopy,
  getMessageFormatter,
  getContentIdsByAddressAndPageType,
  getContentIdsBySetPinPageType,
  getFormatMessageObject,
  formatContentMessage,
  formatCountrySpecificContentId,
  getContentIdsByReplaceCardType,
}

export { resolveReplaceCardMessageId } from './churnflowMessageId'
